// The second step of signing in for somebody with two-step sign-in on: a code
// from their authenticator app, or one of their backup codes.
import { useEffect, useRef, useState } from "react";
import { verifyMfa } from "../utils/mfa";

export default function MfaCodeForm({ mfaToken, onVerified, onCancel, buttonStyle, buttonClass = "" }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [backup, setBackup] = useState(false);
  const input = useRef(null);

  useEffect(() => { input.current?.focus(); }, [backup]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      onVerified(await verifyMfa(mfaToken, code));
    } catch (err) {
      setError(err?.message || "That code didn't work. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6" aria-labelledby="mfa-heading">
      <div className="space-y-2">
        <h3 id="mfa-heading" className="text-lg font-semibold text-ink">Enter your code</h3>
        <p className="text-sm text-secondary">
          {backup
            ? "Type one of the backup codes you saved when you turned on two-step sign-in. Each works once."
            : "Open your authenticator app and type the 6-digit code for Sift Careers."}
        </p>
      </div>

      <div>
        <label htmlFor="mfa-code" className="block text-sm font-medium text-secondary mb-2">
          {backup ? "Backup code" : "6-digit code"}
        </label>
        <input
          id="mfa-code"
          ref={input}
          required
          value={code}
          onChange={(e) => { setCode(e.target.value); setError(""); }}
          inputMode={backup ? "text" : "numeric"}
          autoComplete="one-time-code"
          maxLength={backup ? 16 : 6}
          aria-invalid={!!error}
          aria-describedby={error ? "mfa-error" : undefined}
          disabled={busy}
          className="w-full min-h-[44px] h-12 px-4 bg-surface text-ink placeholder-faint border border-line-control rounded-lg tracking-widest [color-scheme:light] focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors disabled:opacity-50"
        />
      </div>

      {error && (
        <p id="mfa-error" role="alert" className="text-sm font-medium text-red-700">{error}</p>
      )}

      <button
        type="submit"
        disabled={busy || !code.trim()}
        className={`w-full min-h-[44px] h-12 text-white rounded-pill font-medium active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${buttonClass}`}
        style={buttonStyle}
      >
        {busy ? "Checking..." : "Sign In"}
      </button>

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <button type="button" onClick={() => { setBackup(!backup); setCode(""); setError(""); }}
          className="min-h-[44px] text-brand hover:text-brand-hover">
          {backup ? "Use a code from my app" : "Use a backup code instead"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="min-h-[44px] text-tertiary hover:text-ink">
            Start over
          </button>
        )}
      </div>
    </form>
  );
}
